import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  ContractService,
  ContractTemplate,
  ContractData,
  ReviewerApprover,
  ReminderConfig,
  TemplateFillRequest
} from '../contracts/contract.service';

// ==================== CONSTANTS ====================

const CONTRACT_TYPES = [
  { value: 'Purchase Contract', label: 'Purchase Contract', code: 'PUR' },
  { value: 'Service Contract', label: 'Service Contract', code: 'SVC' },
  { value: 'NDA', label: 'Non-Disclosure Agreement', code: 'NDA' },
  { value: 'Lease Agreement', label: 'Lease Agreement', code: 'LSE' },
  { value: 'Employment Contract', label: 'Employment Contract', code: 'EMP' },
  { value: 'Maintenance Agreement', label: 'Maintenance Agreement', code: 'MNT' },
  { value: 'License Agreement', label: 'License Agreement', code: 'LIC' },
  { value: 'Other', label: 'Other', code: 'CTR' }
];

const DEPARTMENTS = [
  { value: 'Information Technology', label: 'Information Technology', code: 'IT' },
  { value: 'Finance', label: 'Finance', code: 'Finance' },
  { value: 'Human Resources', label: 'Human Resources', code: 'HR' },
  { value: 'Administration', label: 'Administration', code: 'ADMIN' },
  { value: 'Legal', label: 'Legal', code: 'Legal' },
  { value: 'Procurement', label: 'Procurement', code: 'LEGAL' },
  { value: 'Marketing', label: 'Marketing', code: 'MARKETING' },
  { value: 'Customer Service', label: 'Customer Service', code: 'CTR' }
];

const CONTRACT_TYPE_FIELDS: { [key: string]: string[] } = {
  'Purchase Contract': ['deliveryTerms', 'paymentTerms', 'warrantyPeriod'],
  'Service Contract': ['serviceDescription', 'slaTerms', 'paymentTerms'],
  'NDA': ['confidentialityPeriod', 'disclosingParty', 'receivingParty'],
  'Lease Agreement': ['propertyAddress', 'leaseTermMonths', 'securityDeposit'],
  'Employment Contract': ['jobTitle', 'probationPeriod', 'noticePeriod', 'benefits'],
  'Maintenance Agreement': ['maintenanceSchedule', 'responseTime', 'coverageScope'],
  'License Agreement': ['licenseType', 'numberOfUsers', 'territory', 'restrictions'],
  'Other': []
};

const CURRENCIES = ['KES', 'USD', 'EUR', 'GBP', 'UGX', 'TZS'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
const LINK_TYPES = [
  { value: 'RELATED', label: 'Related' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'AMENDMENT', label: 'Amendment' },
  { value: 'ADDENDUM', label: 'Addendum' },
  { value: 'RENEWAL', label: 'Renewal' }
];

const DEFAULT_MILESTONE_DAYS = [90, 30, 7];

// ==================== INTERFACES ====================

interface LinkedContract {
  id: string;
  name: string;
  contractNumber?: string;
  contractTitle?: string;
  supplierName?: string;
  contractStatus?: string;
  contractType?: string;
  linkType: string;
}

interface WizardUser {
  id: string;
  displayName: string;
  email?: string;
  userName?: string;
  avatarUrl?: string;
}

@Component({
  selector: 'app-skyplus-contract-wizard',
  templateUrl: './skyplus-contract-wizard.component.html',
  styleUrls: ['./skyplus-contract-wizard.component.scss']
})
export class SkyPlusContractWizardComponent implements OnInit, OnDestroy {

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // ---- Step state ----
  currentStep = 1;
  totalSteps = 6;
  isSubmitting = false;
  submitError = '';
  submitSuccess = false;
  createdContractId = '';
  createdContractNumber = '';


  // ---- Step 1: Source selection ----
  useTemplate = true;          // true = template, false = upload
  templates: ContractTemplate[] = [];
  templatesLoading = false;
  selectedTemplate: ContractTemplate | null = null;

  // Upload mode
  uploadedFile: File | null = null;
  uploadPreviewUrl = '';
  isDragOver = false;

  // ---- Step 2: Contract details form ----
  contractForm!: FormGroup;
  contractTypes = CONTRACT_TYPES;
  departmentName = DEPARTMENTS;
  contractTypeFields = CONTRACT_TYPE_FIELDS;
  currencies = CURRENCIES;
  priorities = PRIORITIES;
  selectedContractType = '';
  generatingNumber = false;

  // ---- Step 3: Contract linking ----
  linkSearchTerm = '';
  linkSearchResults: any[] = [];
  linkSearchLoading = false;
  linkedContracts: LinkedContract[] = [];
  linkTypes = LINK_TYPES;
  private linkSearch$ = new Subject<string>();

  // ---- Step 4: Reviewers & Approvers ----
  userSearchTerm = '';
  userSearchResults: WizardUser[] = [];
  userSearchLoading = false;
  reviewers: ReviewerApprover[] = [];
  approvers: ReviewerApprover[] = [];
  signatureTypes = [
    { value: 'digital', label: 'Digital' },
    { value: 'manual', label: 'Manual' },
    { value: 'none', label: 'None' }
  ];
  private userSearch$ = new Subject<string>();

  // ---- Step 5: Reminders ----
  autoGenerateReminders = true;
  milestoneDays: number[] = [...DEFAULT_MILESTONE_DAYS];
  reminders: ReminderConfig[] = [];
  customRecipientEmail = '';

  // ---- Step 6: Preview ----
  previewExpanded = false;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private contractService: ContractService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadTemplates();
    this.setupLinkSearch();
    this.setupUserSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== FORM SETUP ====================

  private buildForm(): void {
    this.contractForm = this.fb.group({
      // Core
      contractTitle: ['', [Validators.required, Validators.minLength(3)]],
      contractType: ['', Validators.required],
      contractNumber: [''],
      supplierName: ['', Validators.required],
      departmentName: ['', Validators.required],
      companyName: [''],
      priority: ['Medium'],
      confidential: [false],
      internalNotes: [''],
      // Financial
      contractAmount: [null],
      currency: ['KES'],
      amountInWords: [''],
      // Dates
      signingDate: [''],
      effectiveDate: ['', Validators.required],
      expiryDate: ['', Validators.required],
      // Renewal
      isRenewable: [false],
      autoRenew: [false],
      renewalTermMonths: [null],
      // Vendor
      vendorId: [''],
      vendorEmail: ['', Validators.email],
      vendorPhone: [''],
      vendorAddress: [''],
      vendorContactPerson: [''],
      vendorRegistrationNumber: [''],
      vendorTaxId: [''],
      // Banking
      bankName: [''],
      bankBranch: [''],
      bankAccountNumber: [''],
      bankAccountName: [''],
      swiftCode: [''],
      // Purchase Contract
      deliveryTerms: [''],
      paymentTerms: [''],
      warrantyPeriod: [null],
      // Service Contract
      serviceDescription: [''],
      slaTerms: [''],
      // NDA
      confidentialityPeriod: [null],
      disclosingParty: [''],
      receivingParty: [''],
      // Lease
      propertyAddress: [''],
      leaseTermMonths: [null],
      securityDeposit: [null],
      // Employment
      jobTitle: [''],
      probationPeriod: [null],
      noticePeriod: [null],
      benefits: [''],
      // Maintenance
      maintenanceSchedule: [''],
      responseTime: [null],
      coverageScope: [''],
      // License
      licenseType: [''],
      numberOfUsers: [null],
      territory: [''],
      restrictions: ['']
    });

    this.contractForm.get('contractType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        this.selectedContractType = type;
        this.onContractTypeChange(type);
      });

    this.contractForm.get('expiryDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.autoGenerateReminders) {
          this.regenerateReminders();
        }
      });
  }

  // ==================== STEP 1: SOURCE ====================

  selectSource(useTemplate: boolean): void {
    this.useTemplate = useTemplate;
    this.selectedTemplate = null;
    this.uploadedFile = null;
    this.uploadPreviewUrl = '';
  }

  loadTemplates(): void {
    this.templatesLoading = true;
    this.contractService.getTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: templates => {
          this.templates = templates;
          this.templatesLoading = false;
        },
        error: () => {
          this.templatesLoading = false;
          this.showSnack('Could not load templates', 'warn');
        }
      });
  }

  selectTemplate(template: ContractTemplate): void {
    this.selectedTemplate = template;
    // Pre-fill contract type if available
    if (template.contractType) {
      this.contractForm.patchValue({ contractType: template.contractType });
    }
  }

  // ---- Upload handling ----

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
     event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  private handleFile(file: File): void {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith('.docx')) {
      this.showSnack('Please upload a DOCX file', 'warn');
      return;
    }
    this.uploadedFile = file;
    this.uploadPreviewUrl = file.name;
    this.selectedTemplate = null;
  }

  removeUploadedFile(): void {
    this.uploadedFile = null;
    this.uploadPreviewUrl = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  get step1Valid(): boolean {
    if (this.useTemplate) return this.selectedTemplate !== null;
    return this.uploadedFile !== null;
  }

  // ==================== STEP 2: CONTRACT DETAILS ====================

  onContractTypeChange(type: string): void {
    this.selectedContractType = type;
    // Auto-generate contract number when type is selected
    if (type && !this.contractForm.get('contractNumber')?.value) {
      this.generateContractNumber();
    }
  }

  generateContractNumber(): void {
    const type = this.contractForm.get('contractType')?.value || 'Other';
    this.generatingNumber = true;
    this.contractService.generateContractNumber(type)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.contractForm.patchValue({ contractNumber: result.contractNumber });
          this.generatingNumber = false;
        },
        error: () => {
          this.generatingNumber = false;
          this.showSnack('Could not generate contract number', 'warn');
        }
      });
  }

  getTypeSpecificFields(): string[] {
    return CONTRACT_TYPE_FIELDS[this.selectedContractType] || [];
  }

  fieldLabel(field: string): string {
    const labels: { [key: string]: string } = {
      deliveryTerms: 'Delivery Terms',
      paymentTerms: 'Payment Terms',
      warrantyPeriod: 'Warranty Period (months)',
      serviceDescription: 'Service Description',
      slaTerms: 'SLA Terms',
      confidentialityPeriod: 'Confidentiality Period (months)',
      disclosingParty: 'Disclosing Party',
      receivingParty: 'Receiving Party',
      propertyAddress: 'Property Address',
      leaseTermMonths: 'Lease Term (months)',
      securityDeposit: 'Security Deposit',
      jobTitle: 'Job Title',
      probationPeriod: 'Probation Period (months)',
      noticePeriod: 'Notice Period (months)',
      benefits: 'Benefits',
      maintenanceSchedule: 'Maintenance Schedule',
      responseTime: 'Response Time (hours)',
      coverageScope: 'Coverage Scope',
      licenseType: 'License Type',
      numberOfUsers: 'Number of Users',
      territory: 'Territory',
      restrictions: 'Restrictions'
    };
    return labels[field] || field;
  }

  isNumberField(field: string): boolean {
    const numberFields = [
      'warrantyPeriod', 'confidentialityPeriod', 'leaseTermMonths',
      'securityDeposit', 'probationPeriod', 'noticePeriod',
      'responseTime', 'numberOfUsers'
    ];
    return numberFields.includes(field);
  }

  isTextareaField(field: string): boolean {
    return ['serviceDescription', 'slaTerms', 'benefits', 'coverageScope', 'restrictions'].includes(field);
  }

  get step2Valid(): boolean {
    return this.contractForm.get('contractTitle')!.valid
      && this.contractForm.get('contractType')!.valid
      && this.contractForm.get('supplierName')!.valid
      && this.contractForm.get('departmentName')!.valid
      && this.contractForm.get('effectiveDate')!.valid
      && this.contractForm.get('expiryDate')!.valid;
  }

  // ==================== STEP 3: CONTRACT LINKING ====================

  private setupLinkSearch(): void {
    this.linkSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(term => this.performLinkSearch(term));
  }

  onLinkSearchInput(term: string): void {
    this.linkSearchTerm = term;
    if (term.length >= 2) {
      this.linkSearchLoading = true;
      this.linkSearch$.next(term);
    } else {
      this.linkSearchResults = [];
    }
  }

  private performLinkSearch(term: string): void {
    this.contractService.searchAllContracts(term)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: results => {
          // Exclude already-linked contracts and current contract
          this.linkSearchResults = results.filter(r =>
            !this.linkedContracts.some(lc => lc.id === (r.id || r.nodeId))
          );
          this.linkSearchLoading = false;
        },
        error: () => {
          this.linkSearchLoading = false;
          this.linkSearchResults = [];
        }
      });
  }

  isAlreadyLinked(contract: any): boolean {
    const id = contract.id || contract.nodeId;
    return this.linkedContracts.some(lc => lc.id === id);
  }

  addLinkedContract(contract: any, linkType: string = 'RELATED'): void {
    const id = contract.id || contract.nodeId;
    if (!this.isAlreadyLinked(contract)) {
      this.linkedContracts.push({
        id,
        name: contract.name || contract.documentName || '',
        contractNumber: contract.contractNumber || '',
        contractTitle: contract.contractTitle || contract.name || '',
        supplierName: contract.supplierName || '',
        contractStatus: contract.contractStatus || '',
        contractType: contract.contractType || '',
        linkType
      });
      // Remove from search results
      this.linkSearchResults = this.linkSearchResults.filter(r => (r.id || r.nodeId) !== id);
    }
  }

  setLinkType(linkedContract: LinkedContract, linkType: string): void {
    linkedContract.linkType = linkType;
  }

  removeLinkedContract(index: number): void {
    this.linkedContracts.splice(index, 1);
  }

  clearLinkSearch(): void {
    this.linkSearchTerm = '';
    this.linkSearchResults = [];
  }

  getLinkTypeLabel(value: string): string {
    return LINK_TYPES.find(lt => lt.value === value)?.label || value;
  }

  // ==================== STEP 4: REVIEWERS & APPROVERS ====================

  private setupUserSearch(): void {
    this.userSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(term => this.performUserSearch(term));
  }

  onUserSearchInput(term: string): void {
    this.userSearchTerm = term;
    if (term.length >= 2) {
      this.userSearchLoading = true;
      this.userSearch$.next(term);
    } else {
      this.userSearchResults = [];
    }
  }

  private performUserSearch(term: string): void {
    this.contractService.searchUsers(term)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: users => {
          this.userSearchResults = users;
          this.userSearchLoading = false;
        },
        error: () => {
          this.userSearchLoading = false;
          this.userSearchResults = [];
        }
      });
  }

  isAlreadyReviewer(userId: string): boolean {
    return this.reviewers.some(r => r.id === userId);
  }

  isAlreadyApprover(userId: string): boolean {
    return this.approvers.some(a => a.id === userId);
  }

  addAsReviewer(user: WizardUser): void {
    if (this.reviewers.some(r => r.id === user.id)) {
      this.showSnack(`${user.displayName} is already a reviewer`, 'info');
      return;
    }
    this.reviewers.push({
      id: user.id,
      displayName: user.displayName,
      email: user.email,
      order: this.reviewers.length + 1,
      signatureType: 'digital',
      status: 'PENDING'
    });
  }

  addAsApprover(user: WizardUser): void {
    if (this.approvers.some(a => a.id === user.id)) {
      this.showSnack(`${user.displayName} is already an approver`, 'info');
      return;
    }
    this.approvers.push({
      id: user.id,
      displayName: user.displayName,
      email: user.email,
      order: this.approvers.length + 1,
      signatureType: 'digital',
      status: 'PENDING'
    });
  }

  removeReviewer(index: number): void {
    this.reviewers.splice(index, 1);
    this.reviewers.forEach((r, i) => r.order = i + 1);
  }

  removeApprover(index: number): void {
    this.approvers.splice(index, 1);
    this.approvers.forEach((a, i) => a.order = i + 1);
  }

  moveReviewerUp(index: number): void {
    if (index > 0) {
      [this.reviewers[index - 1], this.reviewers[index]] = [this.reviewers[index], this.reviewers[index - 1]];
      this.reviewers.forEach((r, i) => r.order = i + 1);
    }
  }

  moveReviewerDown(index: number): void {
    if (index < this.reviewers.length - 1) {
      [this.reviewers[index], this.reviewers[index + 1]] = [this.reviewers[index + 1], this.reviewers[index]];
      this.reviewers.forEach((r, i) => r.order = i + 1);
    }
  }

  moveApproverUp(index: number): void {
    if (index > 0) {
      [this.approvers[index - 1], this.approvers[index]] = [this.approvers[index], this.approvers[index - 1]];
      this.approvers.forEach((a, i) => a.order = i + 1);
    }
  }

  moveApproverDown(index: number): void {
    if (index < this.approvers.length - 1) {
      [this.approvers[index], this.approvers[index + 1]] = [this.approvers[index + 1], this.approvers[index]];
      this.approvers.forEach((a, i) => a.order = i + 1);
    }
  }

  getUserInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  getAvatarColor(name: string): string {
    const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#0097a7'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  // ==================== STEP 5: REMINDERS ====================

  onAutoGenerateToggle(): void {
    if (this.autoGenerateReminders) {
      this.regenerateReminders();
    }
  }

  regenerateReminders(): void {
    const expiryDate = this.contractForm.get('expiryDate')?.value;
    if (!expiryDate) return;
    this.reminders = this.contractService.generateDefaultExpiryReminders(
      expiryDate,
      [...this.milestoneDays].sort((a, b) => b - a)
    );
  }

  addMilestone(): void {
    this.milestoneDays.push(14);
    if (this.autoGenerateReminders) this.regenerateReminders();
  }

  removeMilestone(index: number): void {
    if (this.milestoneDays.length > 1) {
      this.milestoneDays.splice(index, 1);
      if (this.autoGenerateReminders) this.regenerateReminders();
    }
  }

  updateMilestone(index: number, value: number): void {
    this.milestoneDays[index] = value;
    if (this.autoGenerateReminders) this.regenerateReminders();
  }

  addCustomReminder(): void {
    const expiryDate = this.contractForm.get('expiryDate')?.value;
    const newReminder: ReminderConfig = {
      id: Math.floor(Math.random() * 100000),
      reminderType: 'CUSTOM',
      purpose: 'Custom reminder',
      reminderDate: expiryDate || new Date().toISOString().split('T')[0],
      priority: 'MEDIUM',
      notifyOwner: true,
      notifyReviewers: false,
      notifyApprovers: false,
      notifyDepartment: false,
      recipients: [],
      sent: false
    };
    this.reminders.push(newReminder);
  }

  removeReminder(index: number): void {
    this.reminders.splice(index, 1);
  }

  addCustomRecipient(reminder: ReminderConfig): void {
    const email = this.customRecipientEmail.trim();
    if (email && this.isValidEmail(email)) {
      if (!reminder.recipients) reminder.recipients = [];
      if (!reminder.recipients.includes(email)) {
        reminder.recipients.push(email);
      }
      this.customRecipientEmail = '';
    } else {
      this.showSnack('Enter a valid email address', 'warn');
    }
  }

  removeCustomRecipient(reminder: ReminderConfig, email: string): void {
    reminder.recipients = (reminder.recipients || []).filter(r => r !== email);
  }

  getReminderPriorityClass(priority: string): string {
    switch (priority) {
      case 'CRITICAL': return 'priority-critical';
      case 'HIGH': return 'priority-high';
      case 'MEDIUM': return 'priority-medium';
      default: return 'priority-low';
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // ==================== STEP 6: PREVIEW ====================

  getPreviewFields(): { label: string; value: any }[] {
    const f = this.contractForm.value;
    const core = [
      { label: 'Contract Title', value: f.contractTitle },
      { label: 'Contract Type', value: f.contractType },
      { label: 'Contract Number', value: f.contractNumber || '(Auto-generated)' },
      { label: 'Supplier / Party', value: f.supplierName },
      { label: 'Department', value: f.departmentName },
      { label: 'Company', value: f.companyName },
      { label: 'Contract Amount', value: f.contractAmount ? `${f.currency} ${(f.contractAmount).toLocaleString()}` : 'N/A' },
      { label: 'Effective Date', value: f.effectiveDate },
      { label: 'Expiry Date', value: f.expiryDate },
      { label: 'Renewable', value: f.isRenewable ? 'Yes' : 'No' },
      { label: 'Priority', value: f.priority },
      { label: 'Confidential', value: f.confidential ? 'Yes' : 'No' }
    ];

    // Type-specific
    const typeFields = (CONTRACT_TYPE_FIELDS[this.selectedContractType] || [])
      .map(field => ({ label: this.fieldLabel(field), value: f[field] || 'N/A' }));

    return [...core, ...typeFields];
  }

  getSourceLabel(): string {
    if (this.useTemplate && this.selectedTemplate) {
      return `Template: ${this.selectedTemplate.name}`;
    }
    if (!this.useTemplate && this.uploadedFile) {
      return `Uploaded: ${this.uploadedFile.name}`;
    }
    return 'None';
  }

  // ==================== NAVIGATION ====================

  canProceed(): boolean {
    switch (this.currentStep) {
      case 1: return this.step1Valid;
      case 2: return this.step2Valid;
      case 3:
      case 4:
      case 5:
      case 6: return true;
      default: return false;
    }
  }

  goNext(): void {
    if (this.currentStep < this.totalSteps && this.canProceed()) {
      if (this.currentStep === 2 && this.autoGenerateReminders) {
        this.regenerateReminders();
      }
      this.currentStep++;
    }
  }

  goPrev(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    // Only allow going back or to current
    if (step <= this.currentStep) {
      this.currentStep = step;
    }
  }

  getStepLabel(step: number): string {
    const labels = ['Source', 'Details', 'Linking', 'Workflow', 'Reminders', 'Preview'];
    return labels[step - 1] || '';
  }

  // ==================== SUBMIT ====================

  saveContract(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';

    const formData: ContractData = this.contractForm.value;

    if (!this.useTemplate && this.uploadedFile) {
      // ---- UPLOAD BRANCH ----
      this.contractService.uploadContractWithMetadata(
        this.uploadedFile,
        formData,
        this.reviewers,
        this.approvers,
        this.reminders,
        this.linkedContracts
      ).subscribe({
        next: response => this.handleSubmitSuccess(response),
        error: err => this.handleSubmitError(err)
      });

    } else if (this.useTemplate && this.selectedTemplate) {
      // ---- TEMPLATE BRANCH ----
      const request: TemplateFillRequest = {
        templateNodeId: this.selectedTemplate.id,
        contractData: formData,
        reviewers: this.reviewers,
        approvers: this.approvers,
        reminders: this.reminders,
        linkedContracts: this.linkedContracts.map(lc => ({
          nodeId: lc.id,
          contractNumber: lc.contractNumber,
          linkType: lc.linkType
        }))
      };
      this.contractService.fillTemplate(request).subscribe({
        next: response => this.handleSubmitSuccess(response),
        error: err => this.handleSubmitError(err)
      });
    }
  }

  private handleSubmitSuccess(response: any): void {
    this.isSubmitting = false;
    this.submitSuccess = true;
    // Template fill returns contractNodeId; upload endpoint returns nodeId — handle both
    this.createdContractId = response.contractNodeId || response.nodeId || '';
    this.createdContractNumber = response.contractNumber || '';
    this.showSnack(`Contract ${this.createdContractNumber} saved successfully`, 'success');
    // Navigate directly to the contract details page so the user sees what they just created
    setTimeout(() => {
      if (this.createdContractId) {
        this.router.navigate(['/contract-details', this.createdContractId]);
      } else {
        this.router.navigate(['/contracts']);
      }
    }, 1500);
  }

  private handleSubmitError(err: any): void {
    this.isSubmitting = false;
    this.submitError = err.message || 'Failed to save contract. Please try again.';
    this.showSnack(this.submitError, 'error');
  }

  cancelWizard(): void {
    this.router.navigate(['/contracts']);
  }

  // ==================== UTILITIES ====================

  private showSnack(message: string, type: 'success' | 'warn' | 'error' | 'info' = 'info'): void {
    const panelClass: { [key: string]: string } = {
      success: 'snack-success',
      warn: 'snack-warn',
      error: 'snack-error',
      info: 'snack-info'
    };
    this.snackBar.open(message, 'Close', {
      duration: 3500,
      panelClass: panelClass[type] || ''
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackById(index: number, item: any): string {
    return item.id || index;
  }
}